import { useState, useEffect, useRef } from 'react';
import { Search, X, ShoppingCart, ArrowRight } from 'lucide-react';
import { fetchProducts, fetchCategories } from '../../utils/api';

const cur = () => window.themeData?.currency ?? '$';

// Cycling accent colour classes for category tags
const TAG_COLORS = [
  'bg-primary/10 text-primary hover:bg-primary hover:text-white',
  'bg-accent1/10 text-accent1 hover:bg-accent1 hover:text-white',
  'bg-accent2/10 text-accent2 hover:bg-accent2 hover:text-white',
  'bg-accent3/10 text-accent3 hover:bg-accent3 hover:text-white',
  'bg-secondary/20 text-yellow-600 hover:bg-secondary hover:text-textDark',
];

// Auto-assign emoji from category name
function catEmoji(name) {
  const n = name.toLowerCase();
  if (/cloth|dress|fashion|wear|shirt|hoodie|apparel/.test(n)) return '👗';
  if (/toy|block|lego|play/.test(n))                            return '🧸';
  if (/cycle|bike|ride|scoot/.test(n))                          return '🚲';
  if (/car|vehicle|electric/.test(n))                           return '🚗';
  if (/book|read|story/.test(n))                                return '📚';
  if (/robot|tech|electronic|gadget/.test(n))                   return '🤖';
  if (/puzzle|game|board/.test(n))                              return '🧩';
  if (/sport|outdoor|ball/.test(n))                             return '⚽';
  if (/art|craft|creat|color/.test(n))                          return '🎨';
  if (/music|instrument/.test(n))                               return '🎵';
  if (/decor|home/.test(n))                                     return '🏠';
  if (/access/.test(n))                                         return '💎';
  // Fallback: deterministic emoji from name hash
  const FALLBACKS = ['🎁', '⭐', '🎮', '🏆', '🎯', '🌟', '🎪', '🧪'];
  const hash = [...name].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return FALLBACKS[Math.abs(hash) % FALLBACKS.length];
}

// Debounce hook
function useDebounce(value, delay) {
  const [dv, setDv] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDv(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return dv;
}

export default function SearchModal({ open, onClose }) {
  const [query,         setQuery]         = useState('');
  const [selectedCat,   setSelectedCat]   = useState(null); // { id, name }
  const [results,       setResults]       = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [categories,    setCategories]    = useState([]);
  const [catsLoading,   setCatsLoading]   = useState(true);
  const inputRef    = useRef(null);
  const debouncedQ  = useDebounce(query, 350);

  // Fetch categories once (cached in module scope)
  useEffect(() => {
    fetchCategories({ per_page: 8 })
      .then(data => setCategories(data))
      .catch(() => {})
      .finally(() => setCatsLoading(false));
  }, []);

  // Auto-focus + reset on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedCat(null);
      setResults([]);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Fetch results — either by text search or by category
  useEffect(() => {
    if (selectedCat) {
      // Category tag clicked — fetch by category ID
      setLoading(true);
      fetchProducts({ category: selectedCat.id, per_page: 6, orderby: 'popularity' })
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
      return;
    }
    if (!debouncedQ.trim()) { setResults([]); return; }
    setLoading(true);
    fetchProducts({ search: debouncedQ, per_page: 6 })
      .then(setResults)
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [debouncedQ, selectedCat]);

  if (!open) return null;

  const siteUrl   = window.themeData?.siteUrl ?? '';
  const shopUrl   = `${siteUrl}/shop/`;
  const hasInput  = query.trim() || selectedCat;
  const noResult  = !loading && hasInput && results.length === 0;
  const hasResult = !loading && results.length > 0;

  function handleProductClick(permalink) {
    onClose();
    if (permalink && permalink !== '#') window.location.href = permalink;
  }

  function handleCatTag(cat) {
    setQuery(cat.name);
    setSelectedCat(cat);
    inputRef.current?.focus();
  }

  function handleInputChange(e) {
    setQuery(e.target.value);
    setSelectedCat(null); // clear category filter when typing manually
  }

  function handleClear() {
    setQuery('');
    setSelectedCat(null);
    setResults([]);
    inputRef.current?.focus();
  }

  const viewAllHref = selectedCat
    ? `${siteUrl}/product-category/${selectedCat.slug ?? ''}/`
    : query
      ? `${shopUrl}?s=${encodeURIComponent(query)}`
      : shopUrl;

  return (
    <div className="fixed inset-0 z-[200] flex flex-col">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-3xl mx-auto mt-16 md:mt-24 px-4">
        <div className="bg-white rounded-3xl shadow-2xl border-4 border-primary/20 overflow-hidden">

          {/* Input row */}
          <div className="flex items-center gap-4 px-6 py-5 border-b-2 border-gray-100">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent1 rounded-xl flex items-center justify-center flex-shrink-0">
              <Search className="w-4 h-4 text-white" />
            </div>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleInputChange}
              placeholder="Search for toys, cycles, electric cars, books…"
              className="flex-1 text-lg md:text-xl font-bold text-textDark outline-none placeholder-gray-300 bg-transparent"
              autoComplete="off"
            />
            {query ? (
              <button
                onClick={handleClear}
                className="w-10 h-10 bg-gray-100 hover:bg-red-50 hover:text-red-500 rounded-xl flex items-center justify-center transition-colors flex-shrink-0 text-gray-500"
                title="Clear"
              >
                <X className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={onClose}
                className="w-10 h-10 bg-gray-100 hover:bg-red-50 hover:text-red-500 rounded-xl flex items-center justify-center transition-colors flex-shrink-0 text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Popular categories — shown when no query */}
          {!query && (
            <div className="px-6 py-4 flex flex-wrap gap-2 border-b border-gray-100 min-h-[60px] items-center">
              <span className="text-xs font-bold text-textLight mr-1 self-center flex-shrink-0">Popular:</span>
              {catsLoading ? (
                // Skeleton pills
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-7 w-24 bg-gray-100 rounded-full animate-pulse" />
                ))
              ) : categories.length > 0 ? (
                categories.slice(0, 8).map((cat, i) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCatTag(cat)}
                    className={`px-3 py-1.5 text-xs font-black rounded-full transition-all ${TAG_COLORS[i % TAG_COLORS.length]}`}
                  >
                    {catEmoji(cat.name)} {cat.name}
                    {cat.count > 0 && (
                      <span className="ml-1 opacity-60 font-bold">({cat.count})</span>
                    )}
                  </button>
                ))
              ) : (
                <span className="text-xs text-textLight">No categories found</span>
              )}
            </div>
          )}

          {/* Active category pill (shown inside input area when category selected) */}
          {selectedCat && (
            <div className="px-6 py-2 border-b border-gray-100 flex items-center gap-2 bg-primary/5">
              <span className="text-xs text-textLight font-bold">Category:</span>
              <span className="flex items-center gap-1.5 text-xs font-black text-primary bg-primary/10 px-3 py-1 rounded-full">
                {catEmoji(selectedCat.name)} {selectedCat.name}
                <button onClick={handleClear} className="ml-1 hover:text-red-500 transition-colors">
                  <X className="w-3 h-3" />
                </button>
              </span>
            </div>
          )}

          {/* Results area */}
          <div className="max-h-[60vh] overflow-y-auto">

            {/* Loading */}
            {loading && (
              <div className="flex items-center justify-center py-10">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {/* Default empty state */}
            {!hasInput && !loading && (
              <div className="px-6 py-10 text-center">
                <div className="text-6xl mb-4">🔍</div>
                <p className="font-black text-textDark text-lg mb-2">What are you looking for?</p>
                <p className="text-textLight text-sm">Type to search or pick a category above!</p>
              </div>
            )}

            {/* No results */}
            {noResult && (
              <div className="px-6 py-10 text-center">
                <div className="text-6xl mb-4">😔</div>
                <p className="font-black text-textDark text-lg mb-2">No results found</p>
                <p className="text-textLight text-sm">Try different keywords or browse our categories</p>
              </div>
            )}

            {/* Results grid */}
            {hasResult && (
              <div className="px-4 py-4">
                <p className="text-xs font-black text-textLight px-2 pb-3">
                  {results.length} result{results.length !== 1 ? 's' : ''} found
                  {selectedCat ? ` in ${selectedCat.name}` : ''}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {results.map((p) => {
                    const price        = parseFloat(p.price ?? 0);
                    const regularPrice = parseFloat(p.regular_price ?? 0);
                    const onSale       = p.on_sale && regularPrice > price;
                    const image        = p.images?.[0]?.src;
                    const badge        = p.featured ? '⭐ FEATURED' : p.on_sale ? '🏷️ SALE' : null;
                    return (
                      <div
                        key={p.id}
                        onClick={() => handleProductClick(p.permalink)}
                        className="flex items-center gap-4 bg-white border-2 border-gray-100 hover:border-primary/30 rounded-2xl p-3 group transition-all hover:shadow-md cursor-pointer"
                      >
                        <div className="w-16 h-16 bg-gradient-to-br from-primary/10 to-accent1/10 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden group-hover:scale-110 transition-transform">
                          {image
                            ? <img src={image} alt={p.name} className="w-full h-full object-contain" />
                            : <span className="text-3xl">🎁</span>
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          {badge && (
                            <span className="text-xs font-black text-white bg-gradient-to-r from-accent3 to-green-500 px-2 py-0.5 rounded-full mb-1 inline-block">{badge}</span>
                          )}
                          <h4 className="font-black text-textDark text-sm leading-tight truncate">{p.name}</h4>
                          <p className="text-xs text-textLight mb-1">{p.categories?.[0]?.name ?? ''}</p>
                          <div className="flex items-center gap-1.5">
                            <span className="font-black text-primary text-base">{cur()}{price.toFixed(2)}</span>
                            {onSale && <span className="text-xs text-gray-400 line-through">{cur()}{regularPrice.toFixed(2)}</span>}
                          </div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleProductClick(p.permalink); }}
                          className="flex-shrink-0 w-9 h-9 bg-gradient-to-br from-primary to-accent1 text-white rounded-xl flex items-center justify-center hover:shadow-lg transition-all transform hover:scale-110"
                          title="View product"
                        >
                          <ShoppingCart className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-textLight">
              <kbd className="bg-white border border-gray-200 rounded px-1.5 py-0.5 text-xs font-bold">ESC</kbd> to close
            </span>
            <a
              href={viewAllHref}
              onClick={onClose}
              className="text-xs font-black text-primary hover:text-primaryHover transition-colors flex items-center gap-1"
            >
              View All {selectedCat ? selectedCat.name : 'Products'} <ArrowRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
